import React, { useEffect, useState } from 'react';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import TextInput from '../TextInput';
import { ToxnotReactModal, ToxnotReactModalBody } from '../ToxnotReactModal';

interface SubstanceSearchProps {
    onSubstanceChosen: (casrn: string, chemicalName: string) => void;
    modalTitle: string;
    modalId: string;
    encodedReferringUrlForAddToMySubstances?: string;
};

interface SearchResult {
    substanceId: string;
    privateSubstanceId: string;
    casNumber: string;
    chemicalName: string;
    alsoCalled: string;
    overallScore: string;
    biodegradabilityStatus: string;
    ecNumbers: string[];
};

interface SearchResultSet {
    result: string;
    resultsList: SearchResult[];
    pageNum: number;
    pageStartRowIndex: number;
    totalNumRows: number;
    numPagesOfPrivateSubstanceData: number;
    numPagesOfToxnotSubstanceData: number;
    totalNumPages: number;
};

const SubstanceSearchModal: React.FC<SubstanceSearchProps> = ({ modalId, modalTitle, encodedReferringUrlForAddToMySubstances, onSubstanceChosen }) => {
    const _serviceClient = new ToxnotServiceClient();
    const [visibility, setVisibility] = useState({
        searchingAlert: false,
        errorSearching: false,
        nextPagePrompt: false,
        prevButton: false,
        nextButton: false,
        noResults: false,
        numOfResults: false
    });
    const [queryValue, setQueryValue] = useState('');
    const defaultValue = {
        result: "",
        resultsList: [],
        pageNum: 0,
        pageStartRowIndex: 0,
        totalNumRows: 0,
        numPagesOfPrivateSubstanceData: 0,
        numPagesOfToxnotSubstanceData: 0,
        totalNumPages: 0,
    };
    const [searchResult, setSearchResult] = useState<SearchResultSet>(defaultValue);

    const queryValChange = (newValue:string) => {
        setQueryValue(newValue);
    }

    const searchSubstance = async (pageNum: number) => {
        setVisibility({
            searchingAlert: true,
            errorSearching: false,
            nextPagePrompt: false,
            prevButton: false,
            nextButton: false,
            noResults: false,
            numOfResults: false
        });

        const response = await _serviceClient.search(queryValue, pageNum.toString());

        if (response?.result == "Ok") {
            setSearchResult(response);

            setVisibility(prevState => ({
                ...prevState,
                searchingAlert: false,
                numOfResults: true
            }));

            if (response?.resultsList.length == 0) {
                setVisibility(prevState => ({
                    ...prevState,
                    noResults: true
                }));
            }
            else {
                if (response!.pageNum != response!.totalNumPages - 1)
                    setVisibility(prevState => ({
                        ...prevState,
                        nextButton: true
                    }));
                if (response!.pageNum > 0)
                    setVisibility(prevState => ({
                        ...prevState,
                        prevButton: true
                    }));
                if (response!.numPagesOfPrivateSubstanceData - 1 >= response!.pageNum && response!.numPagesOfToxnotSubstanceData != 0) {
                    setVisibility(prevState => ({
                        ...prevState,
                        nextPagePrompt: true
                    }));
                }
            }
        } else {
            setVisibility(prevState => ({
                ...prevState,
                errorSearching: true
            }));
        }
    }

    const onSelectSubstance = (casNum: string, chemName: string, e: React.MouseEvent<HTMLButtonElement>) => {
        onSubstanceChosen(casNum, chemName);
        $("#" + modalId).modal("hide");
    }

    const clearSearch = () => {
        setQueryValue('');
        setVisibility({
            searchingAlert: false,
            errorSearching: false,
            nextPagePrompt: false,
            prevButton: false,
            nextButton: false,
            noResults: false,
            numOfResults: false
        });
        setSearchResult(defaultValue);
    }

    const calculatePageItems = (startIndex: number, totalRows: number) => {
        let lastIndex = startIndex + searchResult.resultsList.length - 1;
        return (startIndex + 1).toString() + " - " + (lastIndex + 1).toString() + " of " + totalRows.toString() + " result" + (totalRows === 1 ? "" : "s");
    }

    $("#"+modalId).off("hidden.bs.modal").on("hidden.bs.modal", () => {
        clearSearch();
    })

    const searchSubstance_onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key != "Enter") {
            return;
        }

        searchSubstance(0);
    }

    return (
        <ToxnotReactModal modalId={modalId} localizedTitle={modalTitle} additionalClasses="substance-selection-modal" >
            <ToxnotReactModalBody>
                <p>
                    Search by chemical name, Registry Number, or European Commission (EC) Number.
                </p>
                <div className="input-group">
                    <TextInput value={queryValue} onValueChange={queryValChange} placeholder="Search for..." onKeyDown={searchSubstance_onKeyDown}></TextInput>
                    <span className="input-group-btn">
                        <button id="initiate-search-button" className="btn btn-default" onClick={(e) => { searchSubstance(0) }} type="button"><span className="glyphicon glyphicon-search" aria-hidden="true"></span></button>
                    </span>
                </div>
                <div id="edit-inventory-search-results-container">
                    {visibility.searchingAlert ? (<div className={"alert alert-info"} role="alert">
                        Searching...
                    </div>) : ""}
                    {visibility.errorSearching ?? (<div className="alert alert-danger" role="alert">
                        Sorry, there was a problem searching. Please try again.
                    </div>)}
                    <div className="edit-inventory-search-status-container">
                        <div className="edit-inventory-search-status">
                            {visibility.numOfResults ? (searchResult.totalNumRows === 0 ? "0 results" : calculatePageItems(searchResult.pageStartRowIndex, searchResult.totalNumRows)) : ""}
                        </div>
                        <div className="edit-inventory-search-actions">
                            {visibility.nextPagePrompt ? (<div className="alert alert-info">These are My Substances results. Use "Next page" to page through to results from Toxnot's database.</div>):""}
                            {visibility.prevButton ? (<button type="button" className="btn btn-default edit-inventory-search-previous-page-button" onClick={(e) => { searchSubstance(searchResult.pageNum - 1) }}>Previous Page</button>) : ""}
                            {visibility.nextButton ? (<button type="button" className="btn btn-default edit-inventory-search-next-page-button" onClick={(e) => { searchSubstance(searchResult.pageNum + 1) }}>Next Page</button>) : ""}
                        </div>
                    </div>
                    {searchResult.totalNumRows > 0 &&
                        <table className="table tn-table">
                            <thead>
                            <tr>
                                <td></td>
                                <td>Chemical Registry Number</td>
                                <td>EC Number</td>
                                <td>Chemical Name</td>
                                <td>Also Called</td>
                                <td>Overall Rating</td>
                            </tr>
                            </thead>
                            <tbody>{searchResult?.resultsList.map((result, resultIndex) => {
                                return (<tr key={resultIndex} className="edit-inventory-search-result-row"
                                            data-index={resultIndex}>
                                    <td>
                                        <button type="button"
                                                className="btn btn-default btn-sm select-search-result-button"
                                                onClick={(e) => onSelectSubstance(result.casNumber, result.chemicalName, e)}>Select
                                        </button>
                                    </td>
                                    <td>{result.casNumber}</td>
                                    <td>{result.ecNumbers.join(', ')}</td>
                                    <td>{result.chemicalName}</td>
                                    <td>{result.alsoCalled}</td>
                                    <td>{result.overallScore}</td>
                                </tr>)
                            })}
                            </tbody>
                        </table>}
                    {visibility.noResults ? (<div id="edit-inventory-no-search-results-message" className="alert" role="alert">
                        Sorry, no results matched the query.<br /><br />
                        <a href={encodedReferringUrlForAddToMySubstances ? ("/Data/ProductPrivateSubstanceData?referrer=" + encodedReferringUrlForAddToMySubstances) : "/Data/ProductPrivateSubstanceData?"} className="btn btn-default">Add to My Substances</a>
                    </div>) : ""}
                    <div className="edit-inventory-search-actions">
                        {visibility.nextPagePrompt ? (<div className="alert alert-info">These are My Substances results. Use "Next page" to page through to results from Toxnot's database.</div>) : ""}
                        {visibility.prevButton ? (<button type="button" className="btn btn-default edit-inventory-search-previous-page-button">Previous Page</button>) : ""}
                        {visibility.nextButton ? (<button type="button" className="btn btn-default edit-inventory-search-next-page-button">Next Page</button>) : ""}
                    </div>
                </div>
            </ToxnotReactModalBody>
        </ToxnotReactModal>
    )
}

export default SubstanceSearchModal;